// frontend/src/settings/SkillGroupSettings.js - Supabase対応版（既存機能維持）
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { supabase } from '../supabase';
import Header from "../components/ui/Header";
import { useOrganization } from '../contexts/OrganizationContext';
import { manageSkillGroups, manageDepartments, manageProcedures, getStaffList } from "../supabase";
import GroupMemberModal from "./GroupMemberModal";
import GroupConditionModal from "./GroupConditionModal"; 
import { evaluateCondition } from "../utils/skillGroupEvaluator";
import { skillScoreMap, skillLevelLabels } from "../constants/skillLevels";
import { writeClientAudit } from "../utils/auditClient";

// =============================================================================
// 条件に合致するスタッフを計算 (PreviewMembers と同じロジックの再利用版)
// =============================================================================
function computeMatchingStaff(conditionGroups, staffList, skillRecords, departments, procedures) {
  if (!conditionGroups || conditionGroups.length === 0) return [];
  const now = new Date();
  const month = now.getMonth() + 1;
  const currentYear = month >= 4 ? now.getFullYear() : now.getFullYear() - 1;
  return staffList.filter((s) => {
    return conditionGroups.every((group) => {
      let matchCount = 0;
      (group.conditions || []).forEach((cond) => {
        const matched = evaluateCondition(cond, staffList, skillRecords, departments, procedures, currentYear);
        if (matched.includes(s.employee_id)) matchCount++;
      });
      return matchCount >= (group.minimumMatch || 1);
    });
  });
}

// 条件タイプのラベル定義
const conditionTypeLabels = {
  dept: "診療科条件",
  procedure: "術式条件", 
  experience: "経験年数条件",
  schoolYear: "学年条件",
  manual: "個人指定条件"
};

// スキルレベル定義
const skillLevelOptions = [
  { value: 0, label: "未経験" },
  { value: 1, label: "見学済み" },
  { value: 2, label: "経験1回" },
  { value: 3, label: "経験2回" },
  { value: 4, label: "経験3回以上" },
  { value: 5, label: "外から見守り" },
  { value: 6, label: "独り立ち" },
  { value: 7, label: "指導可" }
];

// 対象メンバープレビューコンポーネント
const PreviewMembers = React.memo(({ conditionGroups, staffList, departments, procedures, skillRecords }) => {
  const [showPreview, setShowPreview] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [previewMembers, setPreviewMembers] = useState([]);
  
  const calculatePreview = useCallback(async () => {
    if (!showPreview) return;
    
    setCalculating(true);
    await new Promise(resolve => setTimeout(resolve, 0));
    
    // 年度ベースで計算（4月始まり）
    const now = new Date();
    const month = now.getMonth() + 1;
    const currentYear = month >= 4 ? now.getFullYear() : now.getFullYear() - 1;
    
    const members = staffList.filter((s) => {
      return conditionGroups.every((group) => {
        let matchCount = 0;
        group.conditions.forEach((cond) => {
          const matched = evaluateCondition(
            cond,
            staffList,
            skillRecords,
            departments,
            procedures,
            currentYear
          );
          if (matched.includes(s.employee_id)) matchCount++;
        });
        return matchCount >= group.minimumMatch;
      });
    }).map(s => ({ id: s.employee_id, name: `${s.last_name} ${s.first_name}` }));
    
    setPreviewMembers(members);
    setCalculating(false);
  }, [showPreview, staffList, conditionGroups, departments, procedures, skillRecords]);
  
  useEffect(() => {
    calculatePreview();
  }, [calculatePreview]);

  return (
    <div style={{ marginTop: "1rem", marginBottom: "1rem" }}>
      <button
        type="button"
        onClick={() => setShowPreview(!showPreview)}
        style={{
          padding: "0.75rem 1.5rem",
          backgroundColor: showPreview ? "#e3f2fd" : "#f5f5f5",
          border: "1px solid #ddd",
          borderRadius: "6px",
          cursor: "pointer",
          fontSize: "0.95rem",
          fontWeight: "500",
          transition: "all 0.2s",
          display: "flex",
          alignItems: "center",
          gap: "0.5rem"
        }}
      >
        <span>{showPreview ? "▼ 対象メンバーを隠す" : "▶ 対象メンバーをプレビュー"}</span>
        <span style={{ 
          backgroundColor: "#2563eb",
          color: "white",
          padding: "0.2rem 0.6rem",
          borderRadius: "12px",
          fontSize: "0.8rem",
          fontWeight: "bold"
        }}>
          {previewMembers.length}人
        </span>
      </button>
      
      {showPreview && (
        <div style={{
          marginTop: "1rem",
          padding: "1rem",
          backgroundColor: "#f8fafc",
          borderRadius: "6px",
          border: "1px solid #e2e8f0"
        }}>
          {calculating ? (
            <div style={{ textAlign: "center", color: "#666" }}>
              計算中...
            </div>
          ) : previewMembers.length > 0 ? (
            <div>
              <h4 style={{ marginBottom: "0.5rem", color: "#475569" }}>
                対象メンバー ({previewMembers.length}人):
              </h4>
              <div style={{ 
                display: "flex", 
                flexWrap: "wrap", 
                gap: "0.5rem" 
              }}>
                {previewMembers.map((member) => (
                  <span 
                    key={member.id}
                    style={{
                      backgroundColor: "#dbeafe",
                      color: "#1d4ed8",
                      padding: "0.3rem 0.7rem",
                      borderRadius: "16px",
                      fontSize: "0.85rem",
                      fontWeight: "500"
                    }}
                  >
                    {member.name}
                  </span>
                ))}
              </div>
            </div>
          ) : (
            <div style={{ color: "#6b7280", fontStyle: "italic" }}>
              条件に該当するメンバーがいません
            </div>
          )}
        </div>
      )}
    </div>
  );
});

// 条件グループコンポーネント
const ConditionGroup = React.memo(({ 
  group, 
  groupIdx, 
  departments, 
  procedures, 
  staffList,
  updateMinimumMatch,
  removeConditionGroup,
  updateCondition,
  removeCondition,
  addCondition,
  styles 
}) => (
  <div style={styles.conditionGroup}>
    <div style={styles.conditionGroupHeader}>
      <h3 style={styles.conditionGroupTitle}>条件グループ {groupIdx + 1}</h3>
      {groupIdx > 0 && (
        <button
          type="button"
          onClick={() => removeConditionGroup(groupIdx)}
          style={styles.removeGroupButton}
        >
          グループを削除
        </button>
      )}
    </div>
    
    <div style={styles.minimumMatchContainer}>
      <label style={styles.formLabel}>
        最低一致数: 以下の条件のうち
        <select
          value={group.minimumMatch}
          onChange={(e) => updateMinimumMatch(groupIdx, parseInt(e.target.value))}
          style={styles.select}
        >
          {Array.from({ length: Math.max(1, group.conditions.length) }, (_, i) => (
            <option key={i + 1} value={i + 1}>{i + 1}</option>
          ))}
        </select>
        個以上を満たすスタッフ
      </label>
    </div>

    <div style={styles.conditionsContainer}>
      {group.conditions.map((condition, condIdx) => (
        <div key={condIdx} style={styles.conditionItem}>
          <div style={styles.conditionHeader}>
            <span style={styles.conditionTitle}>
              条件 {condIdx + 1}: {conditionTypeLabels[condition.type]}
            </span>
            <button
              type="button"
              onClick={() => removeCondition(groupIdx, condIdx)}
              style={styles.removeButton}
            >
              削除
            </button>
          </div>
          
          {/* 診療科条件 */}
          {condition.type === "dept" && (
            <div style={styles.conditionContent}>
              <select
                value={condition.department}
                onChange={(e) => updateCondition(groupIdx, condIdx, { department: e.target.value })}
                style={styles.select}
              >
                <option value="">診療科を選択</option>
                {departments.map((dept) => (
                  <option key={dept.id} value={dept.name}>{dept.name}</option>
                ))}
              </select>
              
              <div style={styles.levelSelection}>
                <span style={styles.formLabel}>受け入れ可能レベル:</span>
                <div style={styles.checkboxGrid}>
                  {skillLevelOptions.map((level) => (
                    <label key={level.value} style={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={condition.acceptableLevels?.includes(level.label) || false}
                        onChange={(e) => {
                          const currentLevels = condition.acceptableLevels || [];
                          const newLevels = e.target.checked
                            ? [...currentLevels, level.label]
                            : currentLevels.filter(l => l !== level.label);
                          updateCondition(groupIdx, condIdx, { acceptableLevels: newLevels });
                        }}
                        style={styles.checkbox}
                      />
                      {level.label}
                    </label>
                  ))}
                </div>
              </div>
              
              {/* 🆕 達成割合の追加 */}
              <div style={{ marginTop: "1rem" }}>
                <label style={styles.formLabel}>
                  該当術式の最低達成割合:
                  <select
                    value={condition.minRatio !== undefined ? condition.minRatio : 0.01}
                    onChange={(e) => updateCondition(groupIdx, condIdx, { minRatio: parseFloat(e.target.value) })}
                    style={styles.select}
                  >
                    <option value={0.01}>1% 以上（1術式以上）</option>
                    <option value={0.1}>10% 以上</option>
                    <option value={0.2}>20% 以上</option>
                    <option value={0.3}>30% 以上</option>
                    <option value={0.5}>50% 以上</option>
                    <option value={0.7}>70% 以上</option>
                    <option value={0.8}>80% 以上</option>
                    <option value={1.0}>100%（全術式）</option>
                  </select>
                </label>
              </div>
            </div>
          )}

          {/* 術式条件 */}
          {condition.type === "procedure" && (
            <div style={styles.conditionContent}>
              {/* ❌ 削除 */}
              {/* <input
                type="text"
                placeholder="術式名を入力"
                value={condition.procedureName}
                onChange={(e) => updateCondition(groupIdx, condIdx, { procedureName: e.target.value })}
                style={styles.input}
              /> */}
              
              {/* ✅ 追加：ドロップダウン */}
              <select
                value={condition.procedureName || ""}
                onChange={(e) => updateCondition(groupIdx, condIdx, { procedureName: e.target.value })}
                style={styles.select}
              >
                <option value="">術式を選択</option>
                {procedures.map((proc, idx) => (
                  <option key={proc.id || `proc-${idx}`} value={proc.name}>
                    {proc.name} ({departments.find(d => d.id === proc.department_id)?.name})
                  </option>
                ))}
              </select>
              
              {/* 以下の役割選択とレベル選択は変更なし */}
              
              <div style={styles.roleSelection}>
                <span style={styles.formLabel}>対象役割:</span>
                <div style={styles.radioGroup}>
                  {[
                    { value: "both", label: "器械出し・外回り両方" },
                    { value: "scrub", label: "器械出しのみ" },
                    { value: "circulating", label: "外回りのみ" }
                  ].map((role) => (
                    <label key={role.value} style={styles.radioLabel}>
                      <input
                        type="radio"
                        name={`role_${groupIdx}_${condIdx}`}
                        value={role.value}
                        checked={condition.role === role.value}
                        onChange={(e) => updateCondition(groupIdx, condIdx, { role: e.target.value })}
                        style={styles.radio}
                      />
                      {role.label}
                    </label>
                  ))}
                </div>
              </div>
              
              <div style={styles.levelSelection}>
                <span style={styles.formLabel}>受け入れ可能レベル:</span>
                <div style={styles.checkboxGrid}>
                  {skillLevelOptions.map((level) => (
                    <label key={level.value} style={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={condition.acceptableLevels?.includes(level.label) || false}
                        onChange={(e) => {
                          const currentLevels = condition.acceptableLevels || [];
                          const newLevels = e.target.checked
                            ? [...currentLevels, level.label]
                            : currentLevels.filter(l => l !== level.label);
                          updateCondition(groupIdx, condIdx, { acceptableLevels: newLevels });
                        }}
                        style={styles.checkbox}
                      />
                      {level.label}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* 経験年数条件 */}
          {condition.type === "experience" && (
            <div style={styles.conditionContent}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <select
                  value={condition.minExperience !== undefined ? condition.minExperience : 1}
                  onChange={(e) => updateCondition(groupIdx, condIdx, { minExperience: parseInt(e.target.value) })}
                  style={styles.select}
                >
                  {Array.from({ length: 20 }, (_, i) => i + 1).map((year) => (
                    <option key={year} value={year}>{year}年目</option>
                  ))}
                </select>
                <span style={{ whiteSpace: "nowrap" }}>以上</span>
                
                <select
                  value={condition.maxExperience !== undefined ? condition.maxExperience : 20}
                  onChange={(e) => updateCondition(groupIdx, condIdx, { maxExperience: parseInt(e.target.value) })}
                  style={styles.select}
                >
                  {Array.from({ length: 20 }, (_, i) => i + 1).map((year) => (
                    <option key={year} value={year}>{year}年目</option>
                  ))}
                </select>
                <span style={{ whiteSpace: "nowrap" }}>以下</span>
              </div>
            </div>
          )}

          {/* 学年条件 */}
          {condition.type === "schoolYear" && (
            <div style={styles.conditionContent}>
              <select
                value={condition.targetSchoolYear || 1}
                onChange={(e) => updateCondition(groupIdx, condIdx, { targetSchoolYear: parseInt(e.target.value) })}
                style={styles.select}
              >
                {Array.from({ length: 20 }, (_, i) => i + 1).map((year) => (
                  <option key={year} value={year}>{year}年生</option>
                ))}
              </select>
            </div>
          )}

          {/* 個人指定条件 */}
          {condition.type === "manual" && (
            <div style={styles.conditionContent}>
              <div style={styles.manualMemberSelection}>
                <span style={styles.formLabel}>対象スタッフを選択:</span>
                <div style={styles.staffGrid}>
                  {staffList.map((staff) => (
                    <label key={staff.employee_id} style={styles.checkboxLabel}>
                      <input
                        type="checkbox"
                        checked={condition.manualMembers?.includes(staff.employee_id) || false}
                        onChange={(e) => {
                          const currentMembers = condition.manualMembers || [];
                          const newMembers = e.target.checked
                            ? [...currentMembers, staff.employee_id]
                            : currentMembers.filter(id => id !== staff.employee_id);
                          updateCondition(groupIdx, condIdx, { manualMembers: newMembers });
                        }}
                        style={styles.checkbox}
                      />
                      {staff.last_name} {staff.first_name}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      ))}
      
      <div style={styles.addConditionButtons}>
        <span style={styles.formLabel}>条件を追加:</span>
        <div style={styles.buttonGroup}>
          {Object.entries(conditionTypeLabels).map(([type, label]) => (
            <button
              key={type}
              type="button"
              onClick={() => addCondition(groupIdx, type)}
              style={styles.addButton}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
    </div>
  </div>
));

// グループカードコンポーネント
const GroupCard = React.memo(({ group, onViewMembers, onViewConditions, onDeleteGroup, onViewUnregistered, unregisteredCount = 0, staffList, styles }) => {
  // デバッグログ
  if (group.name === "Aチーム") {
    console.log("🔍 GroupCard デバッグ:", group.name);
    console.log("  - group.members:", group.members);
    console.log("  - group.members[0]:", group.members?.[0]);
    
    // ✅ 追加
    console.log("  - staffList[0].id:", staffList[0]?.id);
    console.log("  - staffList[0].id の型:", typeof staffList[0]?.id);
    
    console.log("  - staffList[0].employee_id:", staffList[0]?.employee_id);
    console.log("  - staffList[0].employee_id の型:", typeof staffList[0]?.employee_id);
    
    // ✅ さらに追加：最初の3人のIDを確認
    console.log("  - 最初の3人のID:", staffList.slice(0, 3).map(s => ({
      id: s.id,
      employee_id: s.employee_id,
      name: `${s.last_name} ${s.first_name}`
    })));
  }
  
  // 実際に存在するスタッフの数を計算
  // members は employee_id 配列で保存（saveGroup で .map(s => s.employee_id)）。
  // ただし旧データには staff.id (UUID) が入っているケースもあるため両対応。
  const actualMemberCount = staffList.filter(s =>
    group.members?.includes(s.employee_id) || group.members?.includes(s.id)
  ).length;
  
  return (
    <div style={styles.groupCard}>
      <div style={styles.groupHeader}>
        <h3 style={styles.groupTitle}>{group.name}</h3>
      </div>
      
      <div style={styles.groupInfo}>
  <span
    style={{
      ...styles.infoTag,
      cursor: 'pointer',
      transition: 'all 0.2s'
    }}
    onClick={() => onViewConditions(group)}
    onMouseEnter={(e) => e.target.style.backgroundColor = '#c7d2fe'}
    onMouseLeave={(e) => e.target.style.backgroundColor = '#e0e7ff'}
  >
    🏷 {group.condition_groups?.length || 0} 条件グループ
  </span>
  <span style={styles.infoTag}>
    👥 {actualMemberCount} 人
  </span>
  {unregisteredCount > 0 && (
    <span
      style={{
        ...styles.infoTag,
        backgroundColor: '#fef3c7',
        color: '#92400e',
        border: '1px solid #fcd34d',
        cursor: 'pointer',
      }}
      onClick={() => onViewUnregistered(group)}
      title="条件に合致するが未登録のスタッフを確認"
    >
      ⚠ 未登録 {unregisteredCount} 名
    </span>
  )}
</div>

      <div style={styles.groupActions}>
        <button
          type="button"
          onClick={() => onViewMembers(group)}
          style={{ ...styles.actionButton, ...styles.viewButton }}
        >
          <span style={styles.buttonIcon}>👁</span>
          メンバー確認
        </button>
        {unregisteredCount > 0 && (
          <button
            type="button"
            onClick={() => onViewUnregistered(group)}
            style={{
              ...styles.actionButton,
              backgroundColor: '#f59e0b',
              color: 'white',
            }}
          >
            <span style={styles.buttonIcon}>➕</span>
            未登録 {unregisteredCount} 名
          </button>
        )}
        <button
          type="button"
          onClick={() => onDeleteGroup(group.id)}
          style={{ ...styles.actionButton, ...styles.deleteGroupButton }}
        >
          <span style={styles.buttonIcon}>🗑</span>
          削除
        </button>
      </div>
    </div>
  );
});

// メインコンポーネント
function SkillGroupSettings() {
  const { currentOrganization } = useOrganization();
  const [groupName, setGroupName] = useState("");
  const [groups, setGroups] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [procedures, setProcedures] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [skillRecords, setSkillRecords] = useState([]);
  const [unregisteredModalGroup, setUnregisteredModalGroup] = useState(null); // 未登録確認モーダル
  const [notifying, setNotifying] = useState(false);
  const [conditionGroups, setConditionGroups] = useState([{
    minimumMatch: 1,
    conditions: [],
  }]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showConditionModal, setShowConditionModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedSuccess, setSavedSuccess] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  
  // データ取得（Supabase対応）
  const fetchInitialData = useCallback(async () => {
    if (!currentOrganization?.id) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // 並列でデータ取得（getStaffList ヘルパー経由で session JWT を送る）
      const [deptData, procData, staffArray, groupData] = await Promise.all([
        manageDepartments('list', { organizationId: currentOrganization.id }),
        manageProcedures('list', { organizationId: currentOrganization.id }),
        getStaffList(currentOrganization.id),
        manageSkillGroups('list', { organizationId: currentOrganization.id })
      ]);
      const staffData = { staff: staffArray };

      // required_rolesをパース
      const parsedProcedures = (procData || []).map(proc => ({
        ...proc,
        required_roles: typeof proc.required_roles === 'string' 
          ? JSON.parse(proc.required_roles) 
          : (proc.required_roles || [])
      }));

      setDepartments(deptData || []);
setProcedures(parsedProcedures);
setStaffList(staffData.staff || []);  // ← .staff を追加
setGroups((groupData || []).map(g => {
  const parsedGroup = {
    ...g,
    condition_groups: typeof g.condition_groups === 'string' 
      ? JSON.parse(g.condition_groups) 
      : g.condition_groups,
    members: typeof g.members === 'string'  // ← 追加
      ? JSON.parse(g.members)               // ← 追加
      : (g.members || [])                   // ← 追加
  };
  
  // デバッグログ
  console.log("📊 グループ:", g.name, "メンバー数:", parsedGroup.members.length);
  
  return parsedGroup;
}));

      // スキルレコードを全件取得（ページネーション方式）
let allSkillRecords = [];
let page = 0;
const pageSize = 1000;

console.log("🔍 スキルレコードを取得中...");
console.log("  - 組織ID:", currentOrganization.id);  // ← デバッグログ追加

while (true) {
  const { data, error } = await supabase
    .from('skill_records')
    .select('*')
    .eq('organization_id', currentOrganization.id)  // ✅ 修正
    .range(page * pageSize, (page + 1) * pageSize - 1);
  
  if (error) {
    console.error("スキルレコード取得エラー:", error);
    break;
  }
  
  if (!data || data.length === 0) break;
  
  allSkillRecords = [...allSkillRecords, ...data];
  console.log(`📦 ページ${page + 1}: ${data.length}件取得（累計: ${allSkillRecords.length}件）`);
  page++;
  
  if (data.length < pageSize) break; // 最後のページ
}

console.log("✅ スキルレコード取得完了:", allSkillRecords.length, "件");
setSkillRecords(allSkillRecords);

    } catch (error) {
      console.error("データ取得エラー:", error);
      alert("データ取得に失敗しました: " + error.message);
    } finally {
      setLoading(false);
    }
  }, [currentOrganization]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  // 条件グループを追加
  const addConditionGroup = useCallback(() => {
    setConditionGroups(prev => [...prev, { minimumMatch: 1, conditions: [] }]);
  }, []);

  // 条件を追加
  const addCondition = useCallback((groupIdx, type) => {
    setConditionGroups(prev => {
      const newGroups = [...prev];
      if (!newGroups[groupIdx]) return prev;
      
      const newCondition = { type };
      
      switch (type) {
        case "dept":
          newCondition.department = "";
          newCondition.acceptableLevels = [];
          break;
        case "procedure":
          newCondition.procedureName = "";
          newCondition.acceptableLevels = [];
          newCondition.role = "both";
          break;
        case "experience":
          newCondition.minExperience = 1;
          newCondition.maxExperience = 20;
          break;
        case "schoolYear":
          newCondition.targetSchoolYear = 1;
          break;
        case "manual":
          newCondition.manualMembers = [];
          break;
      }
      
      newGroups[groupIdx] = {
        ...newGroups[groupIdx],
        conditions: [...newGroups[groupIdx].conditions, newCondition]
      };
      return newGroups;
    });
  }, []);

  // 条件を更新
  const updateCondition = useCallback((groupIdx, condIdx, updates) => {
    setConditionGroups(prev => {
      const newGroups = [...prev];
      if (!newGroups[groupIdx] || !newGroups[groupIdx].conditions[condIdx]) return prev;
      
      newGroups[groupIdx] = {
        ...newGroups[groupIdx],
        conditions: newGroups[groupIdx].conditions.map((cond, idx) => 
          idx === condIdx ? { ...cond, ...updates } : cond
        )
      };
      return newGroups;
    });
  }, []);

  // 条件を削除
  const removeCondition = useCallback((groupIdx, condIdx) => {
    setConditionGroups(prev => {
      const newGroups = [...prev];
      if (!newGroups[groupIdx]) return prev;
      
      newGroups[groupIdx] = {
        ...newGroups[groupIdx],
        conditions: newGroups[groupIdx].conditions.filter((_, idx) => idx !== condIdx)
      };
      return newGroups;
    });
  }, []);

  // 条件グループを削除
  const removeConditionGroup = useCallback((groupIdx) => {
    setConditionGroups(prev => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, idx) => idx !== groupIdx);
    });
  }, []);

  // 最低一致数を更新
  const updateMinimumMatch = useCallback((groupIdx, val) => {
    setConditionGroups(prev => {
      const newGroups = [...prev];
      if (!newGroups[groupIdx]) return prev;
      
      newGroups[groupIdx] = {
        ...newGroups[groupIdx],
        minimumMatch: val
      };
      return newGroups;
    });
  }, []);

  // メンバー計算
  const calculateMembers = useCallback((staffList, conditionGroups) => {
    // 年度ベースで計算
    const now = new Date();
    const month = now.getMonth() + 1;
    const currentYear = month >= 4 ? now.getFullYear() : now.getFullYear() - 1;
    
    
    return staffList.filter((s) => {
      return conditionGroups.every((group) => {
        let matchCount = 0;
        group.conditions.forEach((cond) => {
          const matched = evaluateCondition(
            cond,
            staffList,
            skillRecords,
            departments,
            procedures,
            currentYear
          );
          if (matched.includes(s.employee_id)) matchCount++;
        });
        return matchCount >= group.minimumMatch;
      });
    }).map(s => s.employee_id);
  }, [skillRecords, departments, procedures]);

  // フォームのバリデーション
  const validateForm = useCallback(() => {
    if (!groupName.trim()) {
      alert("グループ名を入力してください");
      return false;
    }
    
    for (let i = 0; i < conditionGroups.length; i++) {
      const group = conditionGroups[i];
      if (group.conditions.length === 0) {
        alert(`条件グループ ${i + 1} に条件が設定されていません`);
        return false;
      }
      
      if (group.minimumMatch < 1 || group.minimumMatch > group.conditions.length) {
        alert(`条件グループ ${i + 1} の最低一致数は1以上、条件数以下である必要があります`);
        return false;
      }
      
      for (let j = 0; j < group.conditions.length; j++) {
        const cond = group.conditions[j];
        
        if (cond.type === "dept" && !cond.department) {
          alert(`条件グループ ${i + 1} の条件 ${j + 1}: 診療科を選択してください`);
          return false;
        }
        
        if (cond.type === "procedure" && !cond.procedureName.trim()) {
          alert(`条件グループ ${i + 1} の条件 ${j + 1}: 術式名を入力してください`);
          return false;
        }
        
        if ((cond.type === "dept" || cond.type === "procedure") && 
            (!cond.acceptableLevels || cond.acceptableLevels.length === 0)) {
          alert(`条件グループ ${i + 1} の条件 ${j + 1}: 受け入れ可能レベルを最低一つ選択してください`);
          return false;
        }
        
        if (cond.type === "manual" && 
            (!cond.manualMembers || cond.manualMembers.length === 0)) {
          alert(`条件グループ ${i + 1} の条件 ${j + 1}: 対象スタッフを最低一人選択してください`);
          return false;
        }
      }
    }
    
    return true;
  }, [groupName, conditionGroups]);

  // グループを保存（Supabase対応）
  // グループを保存（Supabase対応）
  const saveGroup = useCallback(async () => {
    if (!validateForm()) return;
    
    if (!currentOrganization?.id) {
      alert("組織が選択されていません");
      return;
    }
    
    setSaving(true);
    try {
      const members = calculateMembers(staffList, conditionGroups);

      console.log("=== 💾 保存時のデバッグ情報 ===");
      console.log("編集モード:", !!editingGroup);
      console.log("計算されたメンバー数:", members.length);
      console.log("計算されたメンバーID (先頭10件):", members.slice(0, 10));
      console.log("members の型:", Array.isArray(members) ? `Array(${members.length})` : typeof members);
      console.log("members の各要素の型:", members.slice(0, 3).map((m) => typeof m));
      console.log("conditionGroups:", conditionGroups);
      console.log("========================");

      let saveResult;
      if (editingGroup) {
        // 編集モード：update
        saveResult = await manageSkillGroups('update', {
          groupId: editingGroup.id,
          name: groupName,
          conditionGroups,
          members,
          organizationId: currentOrganization.id
        });
      } else {
        // 新規作成モード：add
        saveResult = await manageSkillGroups('add', {
          name: groupName,
          conditionGroups,
          members,
          organizationId: currentOrganization.id
        });
      }

      console.log("📥 保存レスポンス（DB から返ってきた行）:", saveResult);
      if (saveResult && saveResult.members !== undefined) {
        console.log("  ↳ レスポンス members の型:", Array.isArray(saveResult.members) ? `Array(${saveResult.members.length})` : typeof saveResult.members);
        console.log("  ↳ レスポンス members 中身:", saveResult.members);
      }

      setSavedSuccess(true);
      setTimeout(() => setSavedSuccess(false), 3000);
      
      setGroupName("");
      setConditionGroups([{ minimumMatch: 1, conditions: [] }]);
      setEditingGroup(null);
      
      await fetchInitialData();
    } catch (error) {
      console.error("保存エラー:", error);
      alert("グループの保存に失敗しました: " + error.message);
    } finally {
      setSaving(false);
    }
  }, [validateForm, calculateMembers, staffList, conditionGroups, groupName, currentOrganization, fetchInitialData, editingGroup]);

  // グループを削除（Supabase対応）
  const deleteGroup = useCallback(async (id) => {
    if (!window.confirm("このグループを削除しますか？この操作は元に戻せません。")) return;
    
    if (!currentOrganization?.id) {
      alert("組織が選択されていません");
      return;
    }
    
    try {
      await manageSkillGroups('delete', {
        groupId: id,
        organizationId: currentOrganization.id
      });
      setGroups(prev => prev.filter(g => g.id !== id));
    } catch (error) {
      console.error("削除エラー:", error);
      alert("グループの削除に失敗しました: " + error.message);
    }
  }, [currentOrganization]);

  // グループメンバーを表示
  const handleViewMembers = useCallback((group) => {
    setSelectedGroup(group);
    setShowModal(true);
  }, []);

// 条件確認を表示 ← 追加
const handleViewConditions = useCallback((group) => {
  setSelectedGroup(group);
  setShowConditionModal(true);
}, []);

// グループを編集 ← 追加
const handleEditGroup = useCallback((group) => {
  // モーダルを閉じる
  setShowConditionModal(false);
  setSelectedGroup(null);
  
  // フォームに値を読み込む
  setEditingGroup(group);
  setGroupName(group.name);
  setConditionGroups(group.condition_groups || [{ minimumMatch: 1, conditions: [] }]);
  
  // ページ上部にスクロール
  window.scrollTo({ top: 0, behavior: 'smooth' });
}, []);

  // ===========================================================================
  // 条件合致だが未登録のスタッフ (グループごと)
  // ===========================================================================
  const unregisteredByGroup = useMemo(() => {
    const map = {};
    (groups || []).forEach((g) => {
      const matches = computeMatchingStaff(
        g.condition_groups, staffList, skillRecords, departments, procedures
      );
      const registered = new Set(g.members || []);
      const unregistered = matches.filter(
        (s) => !registered.has(s.employee_id) && !registered.has(s.id)
      );
      map[g.id] = unregistered;
    });
    return map;
  }, [groups, staffList, skillRecords, departments, procedures]);

  const totalUnregistered = useMemo(
    () => Object.values(unregisteredByGroup).reduce((a, b) => a + b.length, 0),
    [unregisteredByGroup]
  );
  const groupsWithUnregistered = useMemo(
    () => (groups || []).filter((g) => (unregisteredByGroup[g.id] || []).length > 0),
    [groups, unregisteredByGroup]
  );

  // ===========================================================================
  // 管理者に通知を送信
  // ===========================================================================
  const notifyAdminsOfUnregistered = useCallback(async () => {
    if (!currentOrganization?.id) return;
    if (totalUnregistered === 0) {
      alert("通知すべき未登録スタッフはいません");
      return;
    }
    if (!window.confirm(
      `条件に合致するけど未登録のスタッフが ${totalUnregistered} 名いることを、全ての管理者 (admin) にアプリ内通知で送信します。\n\nよろしいですか？`
    )) return;

    setNotifying(true);
    try {
      // admin ロールのスタッフを取得
      const admins = (staffList || []).filter((s) => s.role === "admin" && s.id);
      if (admins.length === 0) {
        alert("管理者ロールのスタッフが見つかりません");
        return;
      }
      const summary = groupsWithUnregistered
        .map((g) => `・${g.name}: ${unregisteredByGroup[g.id].length} 名`)
        .join("\n");
      const body = `条件に合致するけどグループ未登録のスタッフがいます。\n\n${summary}\n\nスキルグループ設定画面で確認してください。`;
      const rows = admins.map((a) => ({
        organization_id: currentOrganization.id,
        recipient_staff_id: a.id,
        type: "skill_group_mismatch",
        title: `未登録メンバー ${totalUnregistered} 名: スキルグループ確認`,
        body,
      }));
      const { error } = await supabase.from("staff_notifications").insert(rows);
      if (error) throw error;
      await writeClientAudit({
        organizationId: currentOrganization.id,
        actionType: 'create',
        targetTable: 'staff_notifications',
        targetSummary: `スキルグループ未登録メンバー通知を ${admins.length} 名の管理者に送信`,
        changes: { recipientCount: admins.length, totalUnregistered, groups: groupsWithUnregistered.length },
        success: true,
      });
      alert(`${admins.length} 名の管理者に通知を送信しました`);
    } catch (e) {
      console.error("管理者通知エラー:", e);
      alert("通知の送信に失敗しました: " + e.message);
    } finally {
      setNotifying(false);
    }
  }, [currentOrganization?.id, totalUnregistered, groupsWithUnregistered, unregisteredByGroup, staffList]);

  // メンバーリストが更新された時の処理（Supabase対応）
  const handleGroupMemberUpdate = useCallback(async (groupId, updatedMembers) => {
    if (!currentOrganization?.id) return;
  
    try {
      // groupsステートから最新のグループ情報を取得
      const group = groups.find(g => g.id === groupId);
      if (!group) {
        throw new Error('グループが見つかりません');
      }
  
      await manageSkillGroups('update', {
        groupId,
        name: group.name,
        conditionGroups: group.condition_groups,
        members: updatedMembers,
        organizationId: currentOrganization.id
      });
      
      // groupsステートを更新
      const updatedGroups = groups.map(g => 
        g.id === groupId 
          ? { ...g, members: updatedMembers }
          : g
      );
      setGroups(updatedGroups);
      
      // selectedGroupも更新
      const updatedSelectedGroup = updatedGroups.find(g => g.id === groupId);
      if (updatedSelectedGroup) {
        setSelectedGroup(updatedSelectedGroup);
      }
      
    } catch (error) {
      console.error("メンバー更新エラー:", error);
      alert("メンバーの更新に失敗しました");
    }
  }, [currentOrganization, groups]);

  // スタイル定義
  const styles = useMemo(() => ({
    container: {
      minHeight: "100vh",
      backgroundColor: "#f8fafc"
    },
    backLink: {
      display: "inline-block",
      margin: "1rem 2rem",
      padding: "0.5rem 1rem",
      backgroundColor: "#e2e8f0",
      color: "#475569",
      textDecoration: "none",
      borderRadius: "6px",
      fontSize: "0.9rem",
      fontWeight: "500",
      transition: "all 0.2s"
    },
    header: {
      textAlign: "center",
      margin: "2rem 0",
      padding: "0 2rem"
    },
    headerTitle: {
      fontSize: "2rem",
      fontWeight: "bold",
      color: "#1e293b",
      marginBottom: "0.5rem"
    },
    headerSubtitle: {
      fontSize: "1.1rem",
      color: "#64748b",
      fontWeight: "400"
    },
    formCard: {
      backgroundColor: "#fff",
      borderRadius: "8px",
      padding: "2rem",
      margin: "0 2rem 2rem",
      boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
    },
    formTitle: {
      fontSize: "1.5rem",
      fontWeight: "600",
      color: "#1e293b",
      marginBottom: "1.5rem"
    },
    formSection: {
      marginBottom: "1.5rem"
    },
    formLabel: {
      display: "block",
      fontSize: "0.95rem",
      fontWeight: "500",
      color: "#374151",
      marginBottom: "0.5rem"
    },
    input: {
      width: "100%",
      padding: "0.75rem",
      border: "1px solid #d1d5db",
      borderRadius: "6px",
      fontSize: "1rem"
    },
    select: {
      width: "100%",
      padding: "0.75rem",
      border: "1px solid #d1d5db",
      borderRadius: "6px",
      fontSize: "1rem",
      backgroundColor: "#fff"
    },
    conditionGroup: {
      backgroundColor: "#f1f5f9",
      border: "1px solid #cbd5e1",
      borderRadius: "8px",
      padding: "1.5rem",
      marginBottom: "1.5rem"
    },
    conditionGroupHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "1rem"
    },
    conditionGroupTitle: {
      fontSize: "1.2rem",
      fontWeight: "600",
      color: "#334155"
    },
    removeGroupButton: {
      padding: "0.4rem 0.8rem",
      backgroundColor: "#fee2e2",
      color: "#dc2626",
      border: "1px solid #fecaca",
      borderRadius: "4px",
      fontSize: "0.85rem",
      cursor: "pointer"
    },
    minimumMatchContainer: {
      marginBottom: "1rem",
      padding: "1rem",
      backgroundColor: "#e0f2fe",
      borderRadius: "6px"
    },
    conditionsContainer: {
      marginTop: "1rem"
    },
    conditionItem: {
      backgroundColor: "#fff",
      border: "1px solid #e2e8f0",
      borderRadius: "6px",
      padding: "1rem",
      marginBottom: "1rem"
    },
    conditionHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "1rem"
    },
    conditionTitle: {
      fontSize: "1rem",
      fontWeight: "500",
      color: "#475569"
    },
    removeButton: {
      padding: "0.3rem 0.6rem",
      backgroundColor: "#fef2f2",
      color: "#dc2626",
      border: "1px solid #fecaca",
      borderRadius: "4px",
      fontSize: "0.8rem",
      cursor: "pointer"
    },
    conditionContent: {
      marginTop: "0.5rem"
    },
    levelSelection: {
      marginTop: "1rem"
    },
    checkboxGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
      gap: "0.5rem",
      marginTop: "0.5rem"
    },
    checkboxLabel: {
      display: "flex",
      alignItems: "center",
      fontSize: "0.9rem",
      color: "#374151"
    },
    checkbox: {
      marginRight: "0.5rem"
    },
    roleSelection: {
      marginTop: "1rem"
    },
    radioGroup: {
      display: "flex",
      flexDirection: "column",
      gap: "0.5rem",
      marginTop: "0.5rem"
    },
    radioLabel: {
      display: "flex",
      alignItems: "center",
      fontSize: "0.9rem",
      color: "#374151"
    },
    radio: {
      marginRight: "0.5rem"
    },
    rangeInputs: {
      display: "flex",
      alignItems: "center",
      gap: "0.5rem"
    },
    manualMemberSelection: {
      marginTop: "0.5rem"
    },
    staffGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
      gap: "0.5rem",
      marginTop: "0.5rem",
      maxHeight: "200px",
      overflowY: "auto",
      border: "1px solid #e5e7eb",
      padding: "1rem",
      borderRadius: "6px"
    },
    addConditionButtons: {
      marginTop: "1rem",
      padding: "1rem",
      backgroundColor: "#f8fafc",
      borderRadius: "6px",
      border: "2px dashed #cbd5e1"
    },
    buttonGroup: {
      display: "flex",
      flexWrap: "wrap",
      gap: "0.5rem",
      marginTop: "0.5rem"
    },
    addButton: {
      padding: "0.5rem 1rem",
      backgroundColor: "#dbeafe",
      color: "#1d4ed8",
      border: "1px solid #bfdbfe",
      borderRadius: "4px",
      fontSize: "0.85rem",
      cursor: "pointer"
    },
    addGroupButton: {
      padding: "0.75rem 1.5rem",
      backgroundColor: "#f0fdf4",
      color: "#15803d",
      border: "1px solid #bbf7d0",
      borderRadius: "6px",
      fontSize: "0.95rem",
      fontWeight: "500",
      cursor: "pointer",
      marginBottom: "1rem"
    },
    saveButton: {
      backgroundColor: saving ? "#94a3b8" : savedSuccess ? "#10b981" : "#3b82f6",
      color: "#fff",
      border: "none",
      padding: "0.75rem 2rem",
      borderRadius: "6px",
      fontSize: "1rem",
      fontWeight: "600",
      cursor: saving ? "not-allowed" : "pointer",
      transition: "all 0.2s",
      marginTop: "1rem"
    },
    groupsList: {
      backgroundColor: "#fff",
      borderRadius: "8px",
      padding: "2rem",
      margin: "0 2rem 2rem",
      boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
    },
    groupCard: {
      backgroundColor: "#f8fafc",
      border: "1px solid #e2e8f0",
      borderRadius: "8px",
      padding: "1.5rem",
      marginBottom: "1rem"
    },
    groupHeader: {
      marginBottom: "1rem"
    },
    groupTitle: {
      fontSize: "1.2rem",
      fontWeight: "600",
      color: "#1e293b"
    },
    groupInfo: {
      display: "flex",
      gap: "1rem",
      marginBottom: "1rem"
    },
    infoTag: {
      backgroundColor: "#e0e7ff",
      color: "#4338ca",
      padding: "0.25rem 0.75rem",
      borderRadius: "4px",
      fontSize: "0.85rem",
      fontWeight: "500"
    },
    groupActions: {
      display: "flex",
      gap: "0.5rem"
    },
    actionButton: {
      padding: "0.5rem 1rem",
      border: "none",
      borderRadius: "6px",
      fontSize: "0.9rem",
      fontWeight: "500",
      cursor: "pointer",
      transition: "all 0.2s",
      display: "flex",
      alignItems: "center",
      gap: "0.3rem"
    },
    viewButton: {
      backgroundColor: "#eff6ff",
      color: "#2563eb"
    },
    deleteGroupButton: {
      backgroundColor: "#fee2e2",
      color: "#dc2626"
    },
    buttonIcon: {
      fontSize: "1rem"
    },
    infoText: {
      fontSize: "0.9rem",
      color: "#666",
      marginBottom: "1rem"
    },
    loadingContainer: {
      textAlign: "center",
      padding: "2rem"
    }
  }), [saving, savedSuccess]);

  // 組織が選択されていない場合
  if (!currentOrganization?.id) {
    return (
      <div style={styles.container}>
        <Header />
        <Link to="/settings" style={styles.backLink}>← 設定メニューへ戻る</Link>
        <div style={styles.formCard}>
          <div style={{ textAlign: "center", padding: "2rem", color: "#666" }}>
            組織を選択してください
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={styles.container}>
        <Header />
        <Link to="/settings" style={styles.backLink}>← 設定メニューへ戻る</Link>
        <div style={styles.loadingContainer}>
          <h2>データを読み込み中...</h2>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <Header />
      
      <Link to="/settings" style={styles.backLink}>← 設定メニューへ戻る</Link>
      
      <div style={styles.header}>
        <h1 style={styles.headerTitle}>スキルグループ設定</h1>
        <h3 style={styles.headerSubtitle}>
          スキル、経験年数、学年などの条件でスタッフをグループ化できます
        </h3>
      </div>
      
      <div style={styles.formCard}>
      <h2 style={styles.formTitle}>
  {editingGroup ? "スキルグループの編集" : "新しいスキルグループの作成"}
</h2>
        
        <div style={styles.formSection}>
          <label style={styles.formLabel}>グループ名</label>
          <input
            type="text"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            placeholder="例: 心臓血管外科担当可能者"
            style={styles.input}
          />
        </div>
        
        <p style={styles.infoText}>
          条件グループはAND条件で結合されます。すべての条件グループを満たすスタッフがグループに含まれます。
          各条件グループ内では、指定した最低一致数以上の条件を満たすスタッフが対象となります。
        </p>
        
        {/* 条件グループ */}
        {conditionGroups.map((group, groupIdx) => (
          <ConditionGroup
            key={groupIdx}
            group={group}
            groupIdx={groupIdx}
            departments={departments}
            procedures={procedures}
            staffList={staffList}
            updateMinimumMatch={updateMinimumMatch}
            removeConditionGroup={removeConditionGroup}
            updateCondition={updateCondition}
            removeCondition={removeCondition}
            addCondition={addCondition}
            styles={styles}
          />
        ))}
        
        <button
          type="button"
          onClick={addConditionGroup}
          style={styles.addGroupButton}
        >
          ＋ 条件グループを追加
        </button>
        
        {/* 対象メンバープレビュー */}
        <PreviewMembers 
          conditionGroups={conditionGroups}
          staffList={staffList}
          departments={departments}
          procedures={procedures}
          skillRecords={skillRecords}
        />
        
        {/* 編集モード時のキャンセルボタン ← 追加 */}
{editingGroup && (
  <div style={{ textAlign: "center", marginTop: "1rem" }}>
    <button
      type="button"
      onClick={() => {
        if (window.confirm("編集をキャンセルしますか？変更内容は保存されません。")) {
          setGroupName("");
          setConditionGroups([{ minimumMatch: 1, conditions: [] }]);
          setEditingGroup(null);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }}
      style={{
        backgroundColor: "#6c757d",
        color: "#fff",
        border: "none",
        padding: "0.75rem 2rem",
        borderRadius: "6px",
        fontSize: "1rem",
        fontWeight: "600",
        cursor: "pointer",
        marginRight: "1rem"
      }}
    >
      ❌ 編集をキャンセル
    </button>
  </div>
)}

        <div style={{ textAlign: "center" }}>
          <button
            type="button"
            onClick={saveGroup}
            disabled={saving}
            style={styles.saveButton}
          >
            {saving ? "保存中..." : 
 savedSuccess ? "✓ 保存しました" : 
 editingGroup ? "🔄 更新を保存" : "💾 グループを保存"}
          </button>
        </div>
      </div>

      {totalUnregistered > 0 && (
        <div style={{
          background: "#fffbeb",
          border: "1px solid #fcd34d",
          borderRadius: 10,
          padding: "12px 16px",
          marginBottom: 16,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
        }}>
          <div style={{ fontSize: 13, color: "#92400e" }}>
            <strong>条件合致・未登録スタッフが {totalUnregistered} 名</strong>
            {" "}います ({groupsWithUnregistered.length} グループ)。 各グループの「未登録 N 名」ボタンから追加できます。
          </div>
          <button
            type="button"
            onClick={notifyAdminsOfUnregistered}
            disabled={notifying}
            style={{
              padding: "7px 14px",
              background: notifying ? "#9ca3af" : "#f59e0b",
              color: "white",
              border: "none",
              borderRadius: 6,
              fontWeight: 700,
              fontSize: 12,
              cursor: notifying ? "not-allowed" : "pointer",
            }}
          >
            {notifying ? "送信中..." : "管理者全員に通知"}
          </button>
        </div>
      )}

      <div style={styles.groupsList}>
        <h2 style={styles.formTitle}>登録済みグループ({groups.length}件)</h2>
        
        {groups.length === 0 ? (
          <p style={styles.infoText}>登録されているグループはありません</p>
        ) : (
          groups.map((group) => (
            <GroupCard
              key={group.id}
              group={group}
              onViewMembers={handleViewMembers}
              onViewConditions={handleViewConditions}  // ← 追加
              onDeleteGroup={deleteGroup}
              onViewUnregistered={() => setUnregisteredModalGroup(group)}
              unregisteredCount={(unregisteredByGroup[group.id] || []).length}
              staffList={staffList}
              styles={styles}
            />
          ))
        )}
      </div>

      {/* メンバー確認モーダル */}
      {showModal && selectedGroup && (
  <GroupMemberModal
    groupName={selectedGroup.name}
    groupId={selectedGroup.id}
    members={staffList.filter((s) => selectedGroup.members?.includes(s.employee_id) || selectedGroup.members?.includes(s.id))}
    allStaff={staffList}
    onClose={() => {
      setShowModal(false);
      setSelectedGroup(null);
    }}
    onUpdate={handleGroupMemberUpdate}
  />
)}

      {/* 条件確認モーダル ← 追加 */}
{showConditionModal && selectedGroup && (
  <GroupConditionModal
    groupName={selectedGroup.name}
    conditionGroups={selectedGroup.condition_groups}
    onClose={() => {
      setShowConditionModal(false);
      setSelectedGroup(null);
    }}
    onEdit={() => handleEditGroup(selectedGroup)}
  />
)}

      {/* 未登録スタッフ確認モーダル */}
      {unregisteredModalGroup && (
        <UnregisteredMembersModal
          group={unregisteredModalGroup}
          candidates={unregisteredByGroup[unregisteredModalGroup.id] || []}
          onClose={() => setUnregisteredModalGroup(null)}
          onRegister={async (selectedEmployeeIds) => {
            const existingMembers = unregisteredModalGroup.members || [];
            const newMembers = [...new Set([...existingMembers, ...selectedEmployeeIds])];
            await handleGroupMemberUpdate(unregisteredModalGroup.id, newMembers);
            setUnregisteredModalGroup(null);
          }}
        />
      )}
    </div>
  );
}

// =============================================================================
// 未登録スタッフ確認/一括登録モーダル
// =============================================================================
const UnregisteredMembersModal = ({ group, candidates, onClose, onRegister }) => {
  const [selected, setSelected] = useState(() => new Set(candidates.map((s) => s.employee_id)));
  const [saving, setSaving] = useState(false);

  const allChecked = candidates.length > 0 && candidates.every((s) => selected.has(s.employee_id));
  const toggleOne = (empId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(empId)) next.delete(empId); else next.add(empId);
      return next;
    });
  };
  const toggleAll = () => {
    if (allChecked) setSelected(new Set());
    else setSelected(new Set(candidates.map((s) => s.employee_id)));
  };

  const handleRegister = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`${selected.size} 名を「${group.name}」のメンバーに追加します。 よろしいですか？`)) return;
    setSaving(true);
    try {
      await onRegister([...selected]);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(15,23,42,0.6)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "white", width: "min(640px, 94vw)", maxHeight: "90vh",
          borderRadius: 12, boxShadow: "0 24px 60px rgba(0,0,0,0.3)",
          display: "flex", flexDirection: "column",
        }}
      >
        <div style={{ padding: "16px 20px", borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#0f172a" }}>
              「{group.name}」の未登録スタッフ
            </h3>
            <p style={{ margin: "4px 0 0", fontSize: 11, color: "#64748b" }}>
              条件には合致していますが、まだこのグループに登録されていない人の一覧です。
            </p>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 22, color: "#64748b", cursor: "pointer" }}>×</button>
        </div>

        <div style={{ padding: "10px 20px", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: "#334155", cursor: "pointer" }}>
            <input type="checkbox" checked={allChecked} onChange={toggleAll} />
            全て選択 ({candidates.length} 名)
          </label>
          <span style={{ fontSize: 12, color: "#1d4ed8", fontWeight: 700 }}>
            {selected.size} / {candidates.length} 名選択中
          </span>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "8px 20px" }}>
          {candidates.length === 0 ? (
            <div style={{ padding: 30, textAlign: "center", color: "#94a3b8", fontSize: 13 }}>
              未登録のスタッフはいません
            </div>
          ) : (
            candidates.map((s) => (
              <label
                key={s.employee_id}
                style={{
                  display: "flex", alignItems: "center", gap: 10, padding: "8px 10px",
                  borderBottom: "1px solid #f1f5f9", cursor: "pointer",
                  background: selected.has(s.employee_id) ? "#eff6ff" : "transparent",
                }}
              >
                <input
                  type="checkbox"
                  checked={selected.has(s.employee_id)}
                  onChange={() => toggleOne(s.employee_id)}
                />
                <span style={{ fontSize: 13, color: "#0f172a", flex: 1 }}>
                  {s.last_name} {s.first_name}
                </span>
                <span style={{ fontSize: 11, color: "#64748b" }}>{s.employee_id}</span>
              </label>
            ))
          )}
        </div>

        <div style={{ padding: "12px 20px", borderTop: "1px solid #e5e7eb", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button
            type="button"
            onClick={onClose}
            style={{ padding: "7px 14px", background: "#f1f5f9", color: "#334155", border: "1px solid #cbd5e1", borderRadius: 6, fontSize: 12, cursor: "pointer" }}
          >
            キャンセル
          </button>
          <button
            type="button"
            onClick={handleRegister}
            disabled={saving || selected.size === 0}
            style={{
              padding: "7px 14px",
              background: saving || selected.size === 0 ? "#9ca3af" : "#10b981",
              color: "white", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 700,
              cursor: saving || selected.size === 0 ? "not-allowed" : "pointer",
            }}
          >
            {saving ? "登録中..." : `選択した ${selected.size} 名を登録`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SkillGroupSettings;